import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { clampCrop, type SurfImageCrop } from "@vida/surf-core";

export type CropDragMode =
  | "move"
  | "resize-se"
  | "resize-sw"
  | "resize-ne"
  | "resize-nw";

type Options = {
  /** Element whose bounding box is the DISPLAYED image (object-fit: contain result). */
  containerRef: RefObject<HTMLElement | null>;
  crop: SurfImageCrop;
  onChange: (crop: SurfImageCrop) => void;
  /** Minimum crop size (fraction of source). */
  minSize?: number;
};

const DEFAULT_MIN = 0.05;

/**
 * Drag/resize a crop rectangle in the IMAGE display space (0..1 of the shown
 * bitmap). Coordinates map 1:1 to source-fraction crop values because the
 * bitmap is drawn with object-fit: contain filling `containerRef`.
 */
export function useCropDrag({ containerRef, crop, onChange, minSize = DEFAULT_MIN }: Options) {
  const [dragging, setDragging] = useState<CropDragMode | null>(null);
  const startRef = useRef<{ crop: SurfImageCrop; pointerX: number; pointerY: number } | null>(
    null,
  );

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onPointerDown = useCallback(
    (mode: CropDragMode) => (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      startRef.current = { crop: { ...crop }, pointerX: e.clientX, pointerY: e.clientY };
      setDragging(mode);
    },
    [crop],
  );

  useEffect(() => {
    if (!dragging) return;

    const onMove = (e: PointerEvent) => {
      const el = containerRef.current;
      const start = startRef.current;
      if (!el || !start) return;
      const bounds = el.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) return;
      const dx = (e.clientX - start.pointerX) / bounds.width;
      const dy = (e.clientY - start.pointerY) / bounds.height;

      let { x, y, w, h } = start.crop;

      if (dragging === "move") {
        x = start.crop.x + dx;
        y = start.crop.y + dy;
        x = Math.min(Math.max(0, x), 1 - w);
        y = Math.min(Math.max(0, y), 1 - h);
      } else {
        if (dragging.includes("e")) {
          w = Math.min(1 - start.crop.x, Math.max(minSize, start.crop.w + dx));
        }
        if (dragging.includes("w")) {
          const nx = Math.min(
            start.crop.x + start.crop.w - minSize,
            Math.max(0, start.crop.x + dx),
          );
          w = start.crop.x + start.crop.w - nx;
          x = nx;
        }
        if (dragging.includes("s")) {
          h = Math.min(1 - start.crop.y, Math.max(minSize, start.crop.h + dy));
        }
        if (dragging.includes("n")) {
          const ny = Math.min(
            start.crop.y + start.crop.h - minSize,
            Math.max(0, start.crop.y + dy),
          );
          h = start.crop.y + start.crop.h - ny;
          y = ny;
        }
      }

      onChangeRef.current(clampCrop({ x, y, w, h }));
    };

    const onUp = () => {
      setDragging(null);
      startRef.current = null;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragging, containerRef, minSize]);

  return { onPointerDown, dragging };
}
